import React, { useEffect, useRef, useState } from 'react';
import { motion, useInView, useMotionValue, useSpring } from 'framer-motion';

interface CountUpProps {
  value: number | string;
  duration?: number;
  className?: string;
  suffix?: string;
  prefix?: string;
}

export const CountUp: React.FC<CountUpProps> = ({ 
  value, 
  duration = 2, 
  className = '',
  suffix = '',
  prefix = ''
}) => {
  const ref = useRef<HTMLSpanElement>(null);
  const motionValue = useMotionValue(0);
  const springValue = useSpring(motionValue, { 
    damping: 25, 
    stiffness: 100,
    duration: duration * 1000
  });
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [displayValue, setDisplayValue] = useState<string>('0');

  // Parse the value to extract number and text
  const parseValue = (val: number | string) => {
    if (typeof val === 'number') {
      return { number: val, text: '' };
    }
    
    // Handle percentage
    if (val.includes('%')) {
      const number = parseFloat(val.replace('%', ''));
      return { number, text: '%' };
    }
    
    // Handle "millions"
    if (val.toLowerCase().includes('million')) {
      const number = parseFloat(val.replace(/[^0-9.]/g, ''));
      return { number, text: ' millions' };
    }
    
    // Handle other numbers with text
    const numberMatch = val.match(/^(\d+(?:\.\d+)?)/);
    if (numberMatch) {
      const number = parseFloat(numberMatch[1]);
      const text = val.replace(numberMatch[1], '');
      return { number, text };
    }
    
    return { number: 0, text: val };
  };

  const { number: targetNumber, text: textSuffix } = parseValue(value);

  useEffect(() => {
    if (isInView) {
      motionValue.set(targetNumber);
    }
  }, [motionValue, targetNumber, isInView]);

  useEffect(() => {
    const unsubscribe = springValue.on('change', (latest) => {
      // Format the number based on its value
      let formattedNumber: string;
      
      if (latest < 1) {
        formattedNumber = latest.toFixed(2);
      } else if (latest < 10) {
        formattedNumber = latest.toFixed(1);
      } else {
        formattedNumber = Math.round(latest).toString();
      }
      
      // Add prefix and suffix
      setDisplayValue(`${prefix}${formattedNumber}${textSuffix}${suffix}`);
    });

    return unsubscribe;
  }, [springValue, prefix, suffix, textSuffix]);

  return (
    <motion.span
      ref={ref}
      className={className}
      initial={{ opacity: 0 }}
      animate={isInView ? { opacity: 1 } : { opacity: 0 }}
      transition={{ duration: 0.5, delay: 0.2 }}
    >
      {displayValue}
    </motion.span>
  );
};